import { Link } from 'react-router-dom'
import AppHeader from '../components/AppHeader'
import { useAuth } from '../contexts/AuthContext'

export default function LandingPage() {
  const { isAuthenticated, user, logout } = useAuth()

  const rightContent = isAuthenticated ? (
    <>
      <Link to="/customer" className="text-gray-600 hover:text-primary-600 font-medium">
        Customer Portal
      </Link>
      <Link to="/brokers" className="text-gray-600 hover:text-primary-600 font-medium">
        Find a Broker
      </Link>
      <Link to="/broker" className="text-gray-600 hover:text-primary-600 font-medium">
        Broker Portal
      </Link>
      <Link
        to={user?.userType === 'admin' ? '/admin' : user?.userType === 'data_operator' ? '/dataentry' : user?.userType === 'broker' ? '/broker' : '/customer'}
        className="text-gray-600 hover:text-primary-600 font-medium"
      >
        Dashboard
      </Link>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">
          {user?.full_name || user?.username || user?.email || 'User'}
        </span>
        <button
          onClick={logout}
          className="text-gray-600 hover:text-primary-600 font-medium"
        >
          Log out
        </button>
      </div>
    </>
  ) : (
    <>
      <Link to="/login" className="text-gray-600 hover:text-primary-600 font-medium">
        Customer Portal
      </Link>
      <Link to="/brokers" className="text-gray-600 hover:text-primary-600 font-medium">
        Find a Broker
      </Link>
      <Link to="/broker" className="text-gray-600 hover:text-primary-600 font-medium">
        Broker Portal
      </Link>
      <Link to="/login" className="btn-primary">
        Sign In
      </Link>
    </>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100">
      <AppHeader
        showTagline
        variant="landing"
        rightContent={rightContent}
      />

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 tracking-tight">
            Find Your Perfect
            <span className="block text-primary-600">Home Loan</span>
          </h1>
          <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
            Compare mortgage rates from Australia's leading lenders in seconds.
          </p>
          <p className="mt-2 text-xl text-gray-600 max-w-3xl mx-auto">
            Connect with expert brokers. Get the best deal for your dream home.
          </p>
          <p className="mt-3 text-base font-medium text-primary-700 tracking-wide">
            One Platform. Three Stakeholders. Zero Friction.
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <Link to="/customer" className="btn-primary text-lg px-8 py-4">
              Compare Loans Now
            </Link>
            <a href="#how-it-works" className="btn-secondary text-lg px-8 py-4">
              Learn More
            </a>
          </div>
          <p className="mt-6 text-base font-medium text-gray-500 tracking-wide">
            Real rates. Real savings. Real brokers.
          </p>
        </div>
      </div>

      {/* Trust Bar */}
      <div className="bg-white border-y border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex flex-wrap justify-center items-center gap-6 md:gap-10 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Bank-grade encryption</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>ASIC registered</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>100% free for customers</span>
          </div>
        </div>
      </div>

      {/* Social Proof Strip */}
      <div className="bg-gray-50 py-6">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
            Trusted by home buyers across Australia
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white py-12">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-6 rounded-xl bg-primary-50">
              <div className="text-3xl md:text-4xl font-bold text-primary-600">$2.4B+</div>
              <div className="mt-2 text-sm text-gray-600">Loans Compared</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-primary-50">
              <div className="text-3xl md:text-4xl font-bold text-primary-600">500+</div>
              <div className="mt-2 text-sm text-gray-600">Families Helped</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-primary-50">
              <div className="text-3xl md:text-4xl font-bold text-primary-600">&lt; 1s</div>
              <div className="mt-2 text-sm text-gray-600">Comparison Speed</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-primary-50">
              <div className="text-3xl md:text-4xl font-bold text-primary-600">4.8★</div>
              <div className="mt-2 text-sm text-gray-600">Average Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div id="how-it-works" className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">1. Enter Your Details</h3>
              <p className="text-gray-600">Tell us about your loan requirements - amount, property value, and term</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">2. Compare Options</h3>
              <p className="text-gray-600">See personalized results from multiple lenders, ranked by total cost</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">3. Connect with Broker</h3>
              <p className="text-gray-600">Get expert guidance from licensed mortgage brokers</p>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link to="/customer" className="btn-primary text-lg px-8 py-4">
              Start Comparing Now
            </Link>
          </div>
        </div>
      </div>

      {/* Find a Broker CTA */}
      <div className="py-16 bg-gradient-to-r from-blue-50 via-white to-purple-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Talk to an Expert Broker
              </h2>
              <p className="text-lg text-gray-600 mb-2">
                Not sure which loan is right for you? Our licensed mortgage brokers provide free,
                personalised advice to help you find the best deal.
              </p>
              <ul className="mt-4 space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Free service — brokers are paid by lenders, not you
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Licensed and experienced professionals
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  No obligation — browse and connect when you're ready
                </li>
              </ul>
            </div>
            <div className="flex-shrink-0">
              <Link
                to="/brokers"
                className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors shadow-md"
              >
                Find a Broker
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Why Choose LoanLoop?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="card">
              <div className="text-primary-600 mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Instant Comparison</h3>
              <p className="text-gray-600">Get results in under 1 second from 25+ loan products</p>
            </div>
            <div className="card">
              <div className="text-primary-600 mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Secure & Private</h3>
              <p className="text-gray-600">Bank-level encryption keeps your data safe</p>
            </div>
            <div className="card">
              <div className="text-primary-600 mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Save Thousands</h3>
              <p className="text-gray-600">Find the best rate and save on total interest</p>
            </div>
            <div className="card">
              <div className="text-primary-600 mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Expert Support</h3>
              <p className="text-gray-600">Connect with licensed mortgage brokers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Trust & Security Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Trust & Security</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">256-bit SSL Encryption</h3>
              <p className="text-sm text-gray-600">Your data is encrypted in transit and at rest</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">ASIC Registered</h3>
              <p className="text-sm text-gray-600">Operating under Australian financial regulations</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Azure Cloud Infrastructure</h3>
              <p className="text-sm text-gray-600">Enterprise-grade hosting with 99.9% uptime</p>
            </div>
          </div>
        </div>
      </div>

      {/* One Platform Banner */}
      <div className="bg-gradient-to-r from-primary-600 via-teal-500 to-primary-700 py-10">
        <div className="max-w-4xl mx-auto text-center px-4">
          <p className="text-2xl md:text-3xl font-bold text-white tracking-wide">
            One Platform. Three Stakeholders. Zero Friction.
          </p>
          <p className="mt-3 text-primary-100 text-base">
            Customers compare loans. Brokers manage leads. Lenders grow their book. All in one place.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-primary-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Find Your Perfect Home Loan?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands of Australians who have found better rates with LoanLoop
          </p>
          <Link to="/customer" className="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg">
            Get Started Free
          </Link>
          <p className="mt-4 text-sm text-primary-100">
            From application to approval — LoanLoop connects you with the right mortgage, faster
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white font-semibold mb-4">LoanLoop</h3>
              <p className="text-sm">Australia's leading mortgage comparison platform</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/customer" className="hover:text-white">Compare Loans</Link></li>
                <li><Link to="/brokers" className="hover:text-white">Find a Broker</Link></li>
                <li><Link to="/broker" className="hover:text-white">For Brokers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/customer" className="hover:text-white">How It Works</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/legal/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/legal/terms" className="hover:text-white">Terms & Conditions</Link></li>
                <li><Link to="/legal/disclaimer" className="hover:text-white">Disclaimer</Link></li>
                <li><Link to="/legal/fsg" className="hover:text-white">Financial Services Guide</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800">
            <p className="text-center text-sm mb-4">
              &copy; 2026 LoanLoop Pty Ltd (ACN 696 406 112). All rights reserved. | Built with Azure Cloud Infrastructure
            </p>
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xs text-gray-500 mb-2">
                <strong className="text-gray-400">LoanLoop is a comparison service only.</strong> We do not provide credit, lending services, or financial advice.
              </p>
              <p className="text-xs text-gray-500">
                All rates and information displayed are indicative and subject to change without notice.
                Actual rates may vary based on individual circumstances. Lenders may modify offers at any time.
                LoanLoop is not liable for rate changes, offer modifications, or application outcomes.
                Always verify current rates with lenders directly before making decisions.
                Seek independent financial advice for your specific situation.
              </p>
              <p className="text-xs text-gray-500 mt-3">
                LoanLoop Pty Ltd | ACN: 696 406 112 | Australian Credit Licence: Application pending
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
